/**
 * pathsNotTaken — suggests up to 3 registry actions that (a) are
 * reachable from the current workspace state, (b) were not exercised
 * by the completed plan, and (c) compose meaningfully with the plan's
 * outputs.
 *
 * Consumed by the ResultsPane "paths not taken" region. Pure utility
 * module — no UI, no event broker coupling.
 *
 * See criteria/paths-not-taken/criteria.md for full requirements.
 *
 * @module gibbs/results/pathsNotTaken
 */

import {
    getAction,
    getAllActions,
    getCompatibleNextActions,
    checkPreconditions,
    isTypeCompatible,
} from '../registry/index';

/**
 * @typedef {Object} PathSuggestion
 * @property {string} actionId - Resolves in registry
 * @property {string} rationale - ≤ 120 chars
 * @property {string} category - ActionDef.category
 */

const MAX_SUGGESTIONS = 3;
const RATIONALE_MAX = 120;

/**
 * Category ranking for tiebreaking. Higher rank = more relevant as a
 * campaign follow-up. Categories absent from this table get the
 * default rank 0.
 */
const CATEGORY_WEIGHTS = Object.freeze({
    'Simulation & Computation': 3,
    'Property Prediction': 3,
    'Search & Filtering': 2,
    'Fragment Operations': 2,
    'Compound Management': 2,
    'Distributed Jobs': 2,
    'Analysis & Visualization': 1,
    'External Data Retrieval': 1,
    'Target Setup': 0,
    'Workspace Management': -1,
    'Export & Reporting': -1,
    Display: -2,
});

function categoryWeight(cat) {
    return typeof CATEGORY_WEIGHTS[cat] === 'number' ? CATEGORY_WEIGHTS[cat] : 0;
}

function truncate(s, n) {
    if (typeof s !== 'string') return '';
    if (s.length <= n) return s;
    return `${s.slice(0, n - 1)}…`;
}

/**
 * Compose a concise rationale for a suggestion. Names the chaining
 * reason when possible (this action consumes a type produced by the
 * last plan step), otherwise falls back to a generic reachability note.
 */
function composeRationale(candidate, chainReason) {
    if (chainReason) {
        return truncate(`Chains from ${chainReason}: ${candidate.description || candidate.name}`, RATIONALE_MAX);
    }
    return truncate(`Reachable from current workspace: ${candidate.description || candidate.name}`, RATIONALE_MAX);
}

/**
 * Compute the set of actionIds already exercised by a completed plan.
 */
function exercisedActionIds(plan) {
    if (!plan || !Array.isArray(plan.actions)) return new Set();
    const out = new Set();
    for (const a of plan.actions) {
        if (a && typeof a.actionId === 'string') out.add(a.actionId);
    }
    return out;
}

/**
 * Collect all output types produced by the plan's actions.
 */
function planOutputTypes(plan) {
    if (!plan || !Array.isArray(plan.actions)) return [];
    const types = [];
    for (const a of plan.actions) {
        const def = getAction(a.actionId);
        if (!def || !Array.isArray(def.outputs)) continue;
        for (const out of def.outputs) {
            if (out && typeof out.type === 'string') types.push(out.type);
        }
    }
    return types;
}

/**
 * @param {Object} plan - Completed ActionPlan
 * @param {Object} workspace - Live workspace or serialized state
 * @returns {PathSuggestion[]} ≤ 3 suggestions
 */
export function pathsNotTaken(plan, workspace) {
    const exercised = exercisedActionIds(plan);
    const outputTypes = planOutputTypes(plan);

    // Actions that chain directly from a plan output. These rank highest.
    const chainCandidates = new Map(); // actionId -> { def, chainReason }
    for (const t of outputTypes) {
        const compat = getCompatibleNextActions(
            // Need an action whose output has this type. Pick the first
            // registry action with this output type to seed the lookup.
            findSeedActionForOutputType(t)
        );
        for (const def of compat) {
            if (!def || !def.id) continue;
            if (exercised.has(def.id)) continue;
            if (!chainCandidates.has(def.id)) {
                chainCandidates.set(def.id, {
                    def,
                    chainReason: `output type ${t}`,
                });
            }
        }
    }

    // Actions reachable from the current workspace (preconditions met)
    // but not already run. These rank below chain candidates.
    const reachableCandidates = [];
    for (const def of getAllActions()) {
        if (!def || !def.id) continue;
        if (exercised.has(def.id)) continue;
        if (chainCandidates.has(def.id)) continue;
        const { met } = checkPreconditions(def.id, workspace);
        if (!met) continue;
        reachableCandidates.push({ def, chainReason: null });
    }

    // Filter chain candidates by preconditions too — a chaining action
    // may have additional preconditions beyond type compatibility.
    const filteredChain = [];
    for (const [, v] of chainCandidates) {
        const { met } = checkPreconditions(v.def.id, workspace);
        if (met) filteredChain.push(v);
    }

    // Sort: chain candidates first (by category weight desc, id asc),
    // then reachable candidates (by category weight desc, id asc).
    const sortFn = (a, b) => {
        const wa = categoryWeight(a.def.category);
        const wb = categoryWeight(b.def.category);
        if (wa !== wb) return wb - wa;
        return a.def.id.localeCompare(b.def.id);
    };
    filteredChain.sort(sortFn);
    reachableCandidates.sort(sortFn);

    const combined = [...filteredChain, ...reachableCandidates];
    const suggestions = [];
    for (const { def, chainReason } of combined) {
        if (suggestions.length >= MAX_SUGGESTIONS) break;
        // Silent filter — if somehow the actionId is invalid (shouldn't
        // be, but the criterion requires this guarantee), skip.
        if (!getAction(def.id)) continue;
        suggestions.push({
            actionId: def.id,
            rationale: composeRationale(def, chainReason),
            category: def.category,
        });
    }
    return suggestions;
}

/**
 * Find a registry action whose outputs include the given type, so that
 * getCompatibleNextActions has a seed to work from. Returns the first
 * matching actionId or null.
 */
function findSeedActionForOutputType(type) {
    for (const def of getAllActions()) {
        if (!Array.isArray(def.outputs)) continue;
        for (const out of def.outputs) {
            if (out && isTypeCompatible(out.type, type)) return def.id;
        }
    }
    return null;
}
